import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap, catchError, throwError, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LogService } from './log.service';
import { IPageRequest, IPageResult } from '../models/paged-result';

@Injectable({
  providedIn: 'root',
})
export abstract class HttpService<T = any> {
  private baseUrl = environment.apiURL;
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  private pageSize = 10;
  /**
   * @constructor
   * @param {HttpClient} http : Http client for API calls
   */
  constructor(
    private http: HttpClient,
    private logger: LogService,
  ) { }

  /**
   * Creates a new entry on the database
   * @param {any} request : request to be created
   * @returns {any} Entry created
   * @public
   */
  post(request: any, url: string): Observable<T> {
    return this.http
      .post<T>(this.baseUrl + url, request, { headers: this.headers })
      .pipe(
        tap((data) =>
          this.logger.info('create: ' + url + JSON.stringify(data)),
        ),
        catchError(this.handleError),
      );
  }

  /**
   * Multipart request, sending files so we removed the content-type: JSON
   * @param request
   * @param url
   * @returns
   */
  postFile(request: any, url: string): Observable<T> {
    console.log(request);
    return this.http.post<T>(this.baseUrl + url, request).pipe(
      tap((data) =>
        this.logger.info('application created: ' + url + JSON.stringify(data)),
      ),
      catchError(this.handleError),
    );
  }

  patchFile(request: any, url: string): Observable<T> {
    console.log(request);
    return this.http.patch<T>(this.baseUrl + url, request).pipe(
      tap((data) =>
        this.logger.info('application created: ' + url + JSON.stringify(data)),
      ),
      catchError(this.handleError),
    );
  }

  /**
   * Updates an entry
   * @param {any} request : object to update
   * @returns {any} updated object
   * @public
   */
  patch(request: any, url: string): Observable<T> {
    let urlSend = '';
    if (request.id) {
      urlSend = `${this.baseUrl + url + '/' + request.id}`;
    } else {
      urlSend = this.baseUrl + url;
    }
    return this.http.patch<T>(urlSend, request, { headers: this.headers }).pipe(
      tap(() => this.logger.info('update: ' + url + request.id)),
      map(() => request),
      catchError(this.handleError),
    );
  }

  /**
   * Updates an entry
   * @param {any} request : object to update
   * @returns {any} updated object
   * @public
   */
  put(request: any, url: string): Observable<T> {
    if (request.id) {
      url = `${this.baseUrl + url + '/' + request.id}`;
    } else {
      url = this.baseUrl + url;
    }

    return this.http.put<T>(url, request, { headers: this.headers }).pipe(
      tap(() => this.logger.info('update: ' + url + request.id)),
      map(() => request),
      catchError(this.handleError),
    );
  }

  delete(id: number, url?: string, body?: any): Observable<T> {
    const urlSend = `${this.baseUrl + url}`;
    if (id > 0) {
      if (body) {
        return this.http.delete<T>(`${urlSend}/${id}`, { body: body });
      } else {
        return this.http.delete<T>(`${urlSend}/${id}`, {});
      }
    } else {
      return this.http.delete<T>(`${urlSend}`, { body: body });
    }
  }

  /**
   * Get list of items
   * @param {any} IPageRequest: pagination object request
   * @returns {any} list
   * @public
   */
  getAll(
    request: IPageRequest<T>,
    url: string,
    params?: HttpParams,
  ): Observable<IPageResult<T>> {
    if (!params) {
      params = new HttpParams()
        .set('page', (request.page + 1).toString())
        .set('pageSize', this.pageSize)
        .set('sort', request.sort.property.toString())
        .set('sortDirection', request.sort.order.toString());
    }
    return this.http
      .get<IPageResult<T>>(`${this.baseUrl + url}`, {
        params: params,
      })
      .pipe(
        map((response) => {
          response.pageNumber--;
          return response;
        }),
      );
  }

  /**
   * Get a specific item
   * @param {number} id : ID
   * @returns {any} item
   * @public
   */
  getById(id: Number, url: string): Observable<T> {
    const urlSend = `${this.baseUrl + url}`;
    return this.http.get<T>(`${urlSend}/${id}`, {});
  }

  /**
   * Get a specific item
   * @returns {any} item
   * @public
   */
  getByUid(url: string): Observable<T> {
    const urlSend = `${this.baseUrl + url}`;
    return this.http.get<T>(`${urlSend}`, {});
  }

  /**
   * Get the fill for the TID
   * @param {string} id : ID
   * @returns {any} item
   * @public
   */
  getFillByTID(id: string, url: string): Observable<T> {
    const urlFill = `${this.baseUrl + url}`;
    return this.http.get<T>(`${urlFill}/${id}`, {});
  }

  /**
   * Get a list of items
   * @returns {any} array of items
   * @public
   */
  get(url: string, params?: any): Observable<T[]> {
    if (params) {
      return this.http.get<T[]>(this.baseUrl + url, { params: params });
    } else {
      return this.http.get<T[]>(this.baseUrl + url);
    }
  }

  /**
   * Get a list of items
   * @returns {any} array of items
   * @public
   */
  getExcel(url: string, params?: any) {
    if (params) {
      return this.http.get(this.baseUrl + url, {
        params: params,
        responseType: 'blob',
      });
    } else {
      return this.http.get(this.baseUrl + url, { responseType: 'blob' });
    }
  }

  /**
   * Get Lookup of a specific item
   * @returns {any}: object to lookup
   * @public
   */
  getLookup(getUrl: string, params?: any): Observable<T[]> {
    var url = this.baseUrl + getUrl;
    if (params) {
      return this.http.get<T[]>(url, {
        params: params,
      });
    } else {
      return this.http.get<T[]>(url);
    }
  }

  authorize(url: string): Observable<T> {
    return this.http.post<T>(url, { headers: this.headers }).pipe(
      tap((data) =>
        this.logger.info('authorize: ' + url + JSON.stringify(data)),
      ),
      catchError(this.handleError),
    );
  }

  /**
   * Get a list of items
   * @returns {any} array of items
   * @public
   */
  downloadFile(url: string, params?: any) {
    if (params) {
      return this.http.get(this.baseUrl + url, {
        params: params,
        responseType: 'blob',
      });
    } else {
      return this.http.get(this.baseUrl + url, { responseType: 'blob' });
    }
  }

  /**
   * Handles and shows request errors
   * @param {object} error : Error object
   * @returns {any} Error Message
   * @private
   */
  private handleError(err: {
    error: {
      message: any;
      statusCode: any;
      errors: any;
      detail?: any;
      status?: any;
    };
    status: any;
    body: { error: any };
  }) {
    console.log(err);
    let errorMessage: string;
    if (err.error instanceof ErrorEvent) {
      const errorResponse = err.error.errors.generalErrors; // Assuming the API returns an error object
      errorMessage = `Status Code: ${err.error.statusCode}, Message: ${err.error.message
        }, '\n'Errors: ${errorResponse.join('\n ')}`;
    } else {
      if (err.error.errors) {
        errorMessage = `Backend returned code ${err.error.statusCode}: ${err.error.errors}`;
      } else {
        errorMessage = `Backend returned code ${err.error.status}: ${err.error.detail}`;
      }
    }
    console.error(errorMessage);

    return throwError(err.error);
  }
}
